import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { api as API } from '../../mixins/api';
import { CollectionViewLoadingIndicator } from '@repo/ui/components/ui/LoadingIndicator';
import { PrimaryButton } from '@repo/ui/components/buttons/index';
import AppStore from '@/stores/app';
import OAuthConsent, { type OrgBizOption } from '@repo/ui/components/content/forms/OAuthConsent';
import Panel from '@repo/ui/components/layout/Panel';

export default function OAuthAuthorize({ user }) {
  const { t } = useTranslation(['commonPlat']);

  // State
  const clientInfo = AppStore?.state?.oauthConsent?.client;
  const [error, setError] = useState<{ [key: string]: string } | null>(null);

  /**
   * The user has accepted the OAuth request
   */
  const handleAcceptConsent = (context: OrgBizOption) => {
    const originalParams = new URLSearchParams(window.location.search);

    // Make sure we're only sending trusted parameters
    const filteredParams = new URLSearchParams(
      ['client_id', 'redirect_uri', 'response_type', 'scope', 'state'].map((key) => [
        key,
        originalParams.get(key) || '',
      ]),
    );

    // Don't let the API send back a 302 Found
    filteredParams.append('redirect', 'false');
    filteredParams.append('resource', `${context.type}:${context.id}`);

    API.makeRequest(`/oauth/authorize?${filteredParams.toString()}`, 'POST', {}, (_, res) => {
      if (res.status === 200 && res.body.redirect_uri) {
        window.location.href = res.body.redirect_uri;
      } else if ('errorDetails' in res.body) {
        setError({ error: res.body.errorDetails });
      } else {
        setError({ error: t('commonPlat:errors.invalidOAuthClient') });
      }
    });
  };

  /**
   * The user has declined the OAuth request
   */
  const handleDeclineConsent = () => {
    oauthReturn({
      error: 'access_denied',
      error_description: 'User declined authorization',
    });
  };

  // Loading, error states
  if (error) return <OAuthError message={error.error} code={error.errorCode} />;
  if (!clientInfo) return <CollectionViewLoadingIndicator />;

  return (
    <OAuthConsent
      onAccepted={handleAcceptConsent}
      onDeclined={handleDeclineConsent}
      client={clientInfo}
      requestingScopes={clientInfo.scope}
      user={user}
    />
  );
}

const StyledPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;
`;

export type OAuthErrorProps = {
  message: string;
  code: number | string;
  goBack?: boolean;
};

export function OAuthError({ message, code, goBack = true }: OAuthErrorProps) {
  const { t } = useTranslation(['common', 'commonPlat']);

  const onGoBack = () => {
    if (code === 404) {
      return oauthReturn({
        error: 'unauthorized_client',
        error_description: message,
      });
    }

    return oauthReturn({
      error: 'invalid_request',
      error_description: message,
    });
  };

  return (
    <StyledPanel>
      <p>{t('commonPlat:labels.oAuthConsent.invalidRequest')}</p>

      {message}

      {goBack && (
        <footer>
          <PrimaryButton onClick={onGoBack}>{t('common:nav.back')}</PrimaryButton>
        </footer>
      )}
    </StyledPanel>
  );
}

/**
 * Helper function to send the user back to the OAuth consumer service with a response
 *
 * @param params Params to include in the query string
 *
 * @void
 */
function oauthReturn(params: { [key: string]: string }) {
  if (!window) return;

  const currentURI = new URL(window.location.href);
  const redirectUri = new URL(currentURI.searchParams.get('redirect_uri') || '');

  // Add all params including state if present
  Object.entries(params).forEach(([key, value]) => redirectUri.searchParams.append(key, value));

  const state = currentURI.searchParams.get('state');
  if (state) redirectUri.searchParams.append('state', state);

  window.location.href = redirectUri.toString();
}
